import { useQuery } from '@tanstack/react-query';
import { adminService } from '@/lib/api/services/admin.service';
import { Loader2, AlertTriangle, MessageCircle, TrendingUp, Clock, Users, Target, Info, ThumbsUp, Minus, ThumbsDown, Eye } from 'lucide-react';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';

// Helper component for stat cards with tooltips
function StatCard({ 
  icon: Icon, 
  value, 
  label, 
  tooltip 
}: { 
  icon: React.ComponentType<{ className?: string }>; 
  value: string | number; 
  label: string; 
  tooltip: string;
}) {
  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <div className="bg-white rounded-[40px] border border-slate-200 flex-1 h-[214px] flex flex-col items-center justify-center cursor-help">
            <div className="w-16 h-16 rounded-full flex items-center justify-center mb-4 bg-[rgba(70,105,237,0.10)]">
              <Icon className="w-6 h-6 text-[#4669ed]" />
            </div>
            <div className="text-[40px] leading-[42px] font-normal text-slate-900 tracking-tight">
              {value}
            </div>
            <div className="text-sm text-slate-500 mt-2 flex items-center gap-1">
              {label}
              <Info className="w-3 h-3 text-slate-400" />
            </div>
          </div>
        </TooltipTrigger>
        <TooltipContent className="max-w-xs">
          <p className="text-xs">{tooltip}</p>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}

interface ConversationFilters {
  startDate?: string;
  endDate?: string;
  flagged?: boolean;
  role?: 'genius' | 'client';
  sentiment?: 'positive' | 'neutral' | 'negative';
  engagement?: 'low' | 'medium' | 'high';
  tags?: string[];
}

interface ChatAnalyticsProps {
  startDate?: string;
  endDate?: string;
  onOpenDrawer?: (filters?: ConversationFilters) => void;
}

export function ChatAnalytics({ startDate = '', endDate = '', onOpenDrawer }: ChatAnalyticsProps) {

  // Get date range for queries
  const dateParams = {
    startDate: startDate || undefined,
    endDate: endDate || undefined,
  };

  const { data: conversationStats, isLoading: loadingConversations } = useQuery({
    queryKey: ['analytics', 'conversations', startDate, endDate],
    queryFn: () => adminService.getConversationStats(dateParams.startDate, dateParams.endDate),
  });

  const { data: engagementStats, isLoading: loadingEngagement } = useQuery({
    queryKey: ['analytics', 'engagement', startDate, endDate],
    queryFn: () => adminService.getEngagementStats(dateParams.startDate, dateParams.endDate),
  });

  const { data: sentimentStats, isLoading: loadingSentiment } = useQuery({
    queryKey: ['analytics', 'sentiment', startDate, endDate],
    queryFn: () => adminService.getSentimentAnalytics(dateParams.startDate, dateParams.endDate),
  });

  const isLoading = loadingConversations || loadingEngagement || loadingSentiment;

  if (isLoading && !conversationStats && !engagementStats) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-slate-400" />
      </div>
    );
  }

  return (
    <div className="space-y-8">
      {/* Enhanced Conversation Statistics */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <StatCard
          icon={MessageCircle}
          value={conversationStats?.total_conversations || 0}
          label="Total Conversations"
          tooltip="Total number of conversations created within the selected date range"
        />
        <StatCard
          icon={Users}
          value={conversationStats?.total_participants || 0}
          label="Total Participants"
          tooltip="Total number of unique users who participated in conversations within the selected date range"
        />
        <StatCard
          icon={TrendingUp}
          value={conversationStats?.ongoing_conversations || 0}
          label="Ongoing Conversations"
          tooltip="Number of conversations that are currently active (have recent messages)"
        />
        <StatCard
          icon={Target}
          value={conversationStats?.close_rate
            ? `${Number(conversationStats.close_rate).toFixed(1)}%`
            : '0%'}
          label="Opportunity Close Rate"
          tooltip="Percentage of conversations that resulted in a successful outcome or opportunity creation"
        />
      </div>

      {/* Enhanced Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <StatCard
          icon={Clock}
          value={conversationStats?.average_response_time_hours
            ? `${Number(conversationStats.average_response_time_hours).toFixed(2)}h`
            : '0h'}
          label="Avg Response Time"
          tooltip="Average time it takes for users to respond to messages in conversations (in hours)"
        />
        <StatCard
          icon={MessageCircle}
          value={conversationStats?.average_reply_rate_24h
            ? `${Number(conversationStats.average_reply_rate_24h).toFixed(1)}%`
            : '0%'}
          label="Reply Rate (24h)"
          tooltip="Percentage of messages that received a reply within 24 hours"
        />
        <StatCard
          icon={TrendingUp}
          value={conversationStats?.average_exchanges
            ? Number(conversationStats.average_exchanges).toFixed(1)
            : '0'}
          label="Avg Exchanges"
          tooltip="Average number of message exchanges (back-and-forth) per conversation"
        />
        <StatCard
          icon={Target}
          value={conversationStats?.avg_messages_per_conversation
            ? Number(conversationStats.avg_messages_per_conversation).toFixed(1)
            : '0.0'}
          label="Avg Messages/Conversation"
          tooltip="Average number of messages sent per conversation"
        />
      </div>

      {/* View All Conversations Button */}
      {onOpenDrawer && (
        <div className="flex justify-end">
          <button
            onClick={() => onOpenDrawer({
              startDate: startDate || undefined,
              endDate: endDate || undefined,
            })}
            className="flex items-center gap-2 px-5 py-2.5 rounded-full text-sm font-medium text-white"
            style={{
              background: 'linear-gradient(106.66deg, rgba(70, 105, 237, 1) 0%, rgba(54, 84, 233, 1) 100%)',
            }}
          >
            <Eye className="w-4 h-4" />
            View All Conversations
          </button>
        </div>
      )}

      {/* Sentiment Analysis & Engagement Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {/* Sentiment Analysis Card */}
        <div className="bg-white rounded-[40px] border border-slate-200 flex-1 h-[214px] p-6 shadow-sm flex flex-col">
          <div className="mb-3">
            <h3 className="text-sm font-bold text-slate-900">Sentiment Analysis</h3>
            <p className="text-xs text-slate-500 mt-0.5">Click to filter conversations</p>
          </div>
          {sentimentStats?.distribution ? (
            <div className="flex items-center justify-between gap-2 flex-1">
              <div
                onClick={() => {
                  if (onOpenDrawer) {
                    onOpenDrawer({
                      startDate: startDate || undefined,
                      endDate: endDate || undefined,
                      sentiment: 'positive',
                    });
                  }
                }}
                className="flex-1 flex flex-col items-center justify-center p-2 rounded-lg cursor-pointer hover:bg-emerald-50 transition-colors h-full"
              >
                <div className="w-10 h-10 rounded-full flex items-center justify-center bg-emerald-100 mb-2">
                  <ThumbsUp className="w-5 h-5 text-emerald-600" />
                </div>
                <div className="text-xl font-semibold text-slate-900">
                  {sentimentStats.distribution.positive || 0}
                </div>
                <div className="text-xs text-slate-600 mt-1">Positive</div>
                {sentimentStats.total_conversations > 0 && (
                  <div className="text-xs text-slate-400 mt-0.5">
                    {((sentimentStats.distribution.positive / sentimentStats.total_conversations) * 100).toFixed(1)}%
                  </div>
                )}
              </div>
              <div
                onClick={() => {
                  if (onOpenDrawer) {
                    onOpenDrawer({
                      startDate: startDate || undefined,
                      endDate: endDate || undefined,
                      sentiment: 'neutral',
                    });
                  }
                }}
                className="flex-1 flex flex-col items-center justify-center p-2 rounded-lg cursor-pointer hover:bg-slate-50 transition-colors h-full"
              >
                <div className="w-10 h-10 rounded-full flex items-center justify-center bg-[rgba(70,105,237,0.10)] mb-2">
                  <Minus className="w-5 h-5 text-[#4669ed]" />
                </div>
                <div className="text-xl font-semibold text-slate-900">
                  {sentimentStats.distribution.neutral || 0}
                </div>
                <div className="text-xs text-slate-600 mt-1">Neutral</div>
                {sentimentStats.total_conversations > 0 && (
                  <div className="text-xs text-slate-400 mt-0.5">
                    {((sentimentStats.distribution.neutral / sentimentStats.total_conversations) * 100).toFixed(1)}%
                  </div>
                )}
              </div>
              <div
                onClick={() => {
                  if (onOpenDrawer) {
                    onOpenDrawer({
                      startDate: startDate || undefined,
                      endDate: endDate || undefined,
                      sentiment: 'negative',
                    });
                  }
                }}
                className="flex-1 flex flex-col items-center justify-center p-2 rounded-lg cursor-pointer hover:bg-red-50 transition-colors h-full"
              >
                <div className="w-10 h-10 rounded-full flex items-center justify-center bg-red-100 mb-2">
                  <ThumbsDown className="w-5 h-5 text-red-600" />
                </div>
                <div className="text-xl font-semibold text-slate-900">
                  {sentimentStats.distribution.negative || 0}
                </div>
                <div className="text-xs text-slate-600 mt-1">Negative</div>
                {sentimentStats.total_conversations > 0 && (
                  <div className="text-xs text-slate-400 mt-0.5">
                    {((sentimentStats.distribution.negative / sentimentStats.total_conversations) * 100).toFixed(1)}%
                  </div>
                )}
              </div>
            </div>
          ) : (
            <div className="flex items-center justify-center flex-1 text-sm text-slate-400">
              No sentiment data available
            </div>
          )}
        </div>

        {/* Engagement Metrics */}
        <div
          onClick={() => {
            if (onOpenDrawer) {
              onOpenDrawer({
                startDate: startDate || undefined,
                endDate: endDate || undefined,
                engagement: 'low',
              });
            }
          }}
          className="bg-white rounded-[40px] border border-slate-200 flex-1 h-[214px] flex flex-col items-center justify-center cursor-pointer hover:bg-amber-50 hover:border-amber-200 transition-colors"
        >
          <div className="w-16 h-16 rounded-full flex items-center justify-center mb-4 bg-amber-100">
            <TrendingUp className="w-6 h-6 text-amber-600" />
          </div>
          <div className="text-[40px] leading-[42px] font-normal text-slate-900 tracking-tight">
            {engagementStats?.conversations_low_engagement || 0}
          </div>
          <div className="text-sm text-slate-500 mt-2 flex items-center gap-1">
            Low Engagement
            <Info className="w-3 h-3 text-slate-400" />
          </div>
          {engagementStats?.low_engagement_percentage && (
            <div className="text-xs text-slate-400 mt-1">
              {Number(engagementStats.low_engagement_percentage).toFixed(1)}% of total
            </div>
          )}
        </div>
        <div
          onClick={() => {
            if (onOpenDrawer) {
              onOpenDrawer({
                startDate: startDate || undefined,
                endDate: endDate || undefined,
                engagement: 'low', // left_on_read is similar to low engagement
              });
            }
          }}
          className="bg-white rounded-[40px] border border-slate-200 flex-1 h-[214px] flex flex-col items-center justify-center cursor-pointer hover:bg-orange-50 hover:border-orange-200 transition-colors"
        >
          <div className="w-16 h-16 rounded-full flex items-center justify-center mb-4 bg-orange-100">
            <MessageCircle className="w-6 h-6 text-orange-600" />
          </div>
          <div className="text-[40px] leading-[42px] font-normal text-slate-900 tracking-tight">
            {engagementStats?.conversations_left_on_read || 0}
          </div>
          <div className="text-sm text-slate-500 mt-2 flex items-center gap-1">
            Left on Read
            <Info className="w-3 h-3 text-slate-400" />
          </div>
          {engagementStats?.left_on_read_percentage && (
            <div className="text-xs text-slate-400 mt-1">
              {Number(engagementStats.left_on_read_percentage).toFixed(1)}% of total
            </div>
          )}
        </div>
        <div
          onClick={() => {
            if (onOpenDrawer) {
              onOpenDrawer({
                startDate: startDate || undefined,
                endDate: endDate || undefined,
                flagged: true,
              });
            }
          }}
          className="bg-white rounded-[40px] border border-slate-200 flex-1 h-[214px] flex flex-col items-center justify-center cursor-pointer hover:bg-red-50 hover:border-red-200 transition-colors"
        >
          <div className="w-16 h-16 rounded-full flex items-center justify-center mb-4 bg-red-100">
            <AlertTriangle className="w-6 h-6 text-red-600" />
          </div>
          <div className="text-[40px] leading-[42px] font-normal text-slate-900 tracking-tight">
            {engagementStats?.flagged_conversations || 0}
          </div>
          <div className="text-sm text-slate-500 mt-2 flex items-center gap-1">
            Flagged
            <Info className="w-3 h-3 text-slate-400" />
          </div>
          {engagementStats?.total_conversations && engagementStats.total_conversations > 0 && (
            <div className="text-xs text-slate-400 mt-1">
              {(((engagementStats?.flagged_conversations || 0) / engagementStats.total_conversations) * 100).toFixed(1)}% of total
            </div>
          )}
        </div>
      </div>

    </div>
  );
}
